// Modules
import { getTopLeft } from 'ol/extent';
import { and, equalTo, like } from 'ol/format/filter';
import GML2 from 'ol/format/GML2';
import WFS from 'ol/format/WFS';
import ImageLayer from 'ol/layer/Image';
import ImageWMS from 'ol/source/ImageWMS';
import TilegridWMTS from 'ol/tilegrid/WMTS';
import WMTS from 'ol/source/WMTS';
import TileLayer from 'ol/layer/Tile';
import { bbox } from 'ol/loadingstrategy';
import TileWMS from 'ol/source/TileWMS';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Circle from 'ol/style/Circle';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';

import proj4 from 'proj4';
import { get as getProjection } from 'ol/proj';
import { register } from 'ol/proj/proj4';

const layer = {};

// Promise request: https://stackoverflow.com/a/30008115
export const basemapPromise = () => new Promise(getToken);

const getToken = (resolve, reject) => {
  const xhr = new XMLHttpRequest();
  const url = 'https://kart9.nibio.no/map/token_json.jsp';
  xhr.onload = () => {
    if (xhr.status >= 200 && xhr.status < 300) {
      const r = JSON.parse(xhr.responseText);
      resolve(defineLayers(r.token));
    } else {
      reject({ status: xhr.status, statusText: xhr.statusText });
    }
  };
  xhr.onerror = () => {
    reject({ status: xhr.status, statusText: xhr.statusText });
  };
  xhr.open('GET', url, true);
  xhr.send();
};

// Setup projections for map
// global: (to be keeped. Strange enough, it fixes projection for uploaded file)
proj4.defs('EPSG:32632','+proj=utm +zone=32 +ellps=WGS84 +datum=WGS84 +units=m +no_defs');
//proj4.defs('EPSG:32633','+proj=utm +zone=33 +ellps=WGS84 +datum=WGS84 +units=m +no_defs');
//proj4.defs('EPSG:32635','+proj=utm +zone=35 +ellps=WGS84 +datum=WGS84 +units=m +no_defs');

// EUREF89 (Norway)
proj4.defs('EPSG:25832','+proj=utm +zone=32 +ellps=GRS80 +units=m +no_defs');
proj4.defs('EPSG:25833','+proj=utm +zone=33 +ellps=GRS80 +units=m +no_defs');
proj4.defs('EPSG:25835','+proj=utm +zone=35 +ellps=GRS80 +units=m +no_defs');
proj4.defs('EPSG:4258','+proj=longlat +ellps=GRS80 +no_defs');

register(proj4);

// function updateProjection() in functions.js
const epsg = {};
const utm = [32, 33, 35];
utm.forEach(u => epsg[u] = getProjection(`EPSG:258${u}`));

// // extents taken from https://spatialreference.org
// epsg[32].setExtent([265948.8191, 6421521.2254, 677786.3629, 7288831.7014]);
// epsg[33].setExtent([227879.8880, 3932632.6543, 1044484.3835, 8893131.0281]);
// epsg[35].setExtent([261450.8249, 7668433.6301, 619396.7507, 8893131.0281]);

// // extents taken from https://epsg.io
// epsg[32].setExtent([-1877994.66, 3638086.74, 3473041.38, 9494203.2]);
// epsg[33].setExtent([-2465144.8, 3638055.41, 2885759.28, 9493779.8]);
// epsg[35].setExtent([-3669433.9, 3638050.95, 1737349.49, 9567789.69]);

epsg[32].setExtent([239323, 4294418, 760676, 9320086]);
epsg[33].setExtent([269387, 5142865, 731380, 9330126]);
epsg[35].setExtent([330890, 6615141, 669109, 9330120]);

const mapExtent = {};
// taken from http://www.kartverket.no/kart/gratis-kartdata/wms-tjenester/
mapExtent[32] = [-2000000.0, 3500000.0, 3545984.0, 9045984.0];
mapExtent[33] = [-2500000.0, 3500000.0, 3045984.0, 9045984.0];
mapExtent[35] = [-3500000.0, 3500000.0, 2045984.0, 9045984.0];

const matrixIds = new Array(18).fill(0).map((a, i) => i);

// new norge i bilder
const NIBResolutions = [
  21674.7100160867,
  10837.35500804335,
  5418.677504021675,
  2709.3387520108377,
  1354.6693760054188,
  677.3346880027094,
  338.6673440013547,
  169.33367200067735,
  84.66683600033868,
  42.33341800016934,
  21.16670900008467,
  10.583354500042335,
  5.291677250021167,
  2.6458386250105836,
  1.3229193125052918,
  0.6614596562526459,
  0.33072982812632296,
  0.16536491406316148
];

// Background basemaps
const kv = 'https://cache.kartverket.no';
const gatekeeperURL = 'https://gatekeeper{1-3}.geonorge.no/BaatGatekeeper/gk/gk';
const gridResolutions = [
  21664, 10832, 5416, 2708, 1354, 677, 338.5, 169.25, 84.625, 42.3125, 21.15625,
  10.578125, 5.2890625, 2.64453125, 1.322265625, 0.6611328125, 0.33056640625, 0.165283203125
];

const getWmtsUrl = (name, zone, nib, token) => {
  return nib ? `${gatekeeperURL}.nib_utm${zone}_wmts_v2?gkt=${token}` :
    `${kv}/${name}/v1/wmts/1.0.0`;
};


const defineLayers = (token) => {
  const newTileLayer = (id, utm) => {
    const nib = (id === 'flybilder');
    const name = {
      graatoner: 'topograatone',
      flybilder: `Nibcache_UTM${utm}_EUREF89_v2`,
      raster: 'toporaster',
      farger: 'topo'
    };
    const type = 'wmts';
    const source = new WMTS({
      crossOrigin: 'Anonymous',
      url: getWmtsUrl(name[id], utm, nib, token),
      layer: name[id],
      matrixSet: nib ? 'default028mm' : `utm${utm}n`,
      format: `image/${nib ? 'jpg' : ''}png`,
      projection: epsg[utm],
      tileGrid: new TilegridWMTS({
        origin: getTopLeft(mapExtent[utm]),
        resolutions: gridResolutions,
        matrixIds: matrixIds
      }),
      style: 'default' // needed for nib
    });
    return new TileLayer({ properties: { id, type }, source });
  };
  // We could use the same as main map,
  // but I do not want to have dynamic projection on minimaps
  const bg = ['graatoner', 'flybilder', 'raster', 'farger'];
  const wmts = {};
  bg.forEach(id => {
    wmts[id] = {};
    utm.forEach(u => wmts[id][u] = newTileLayer(id, u));
  });
  return wmts;
}; // end defineLayers

// WMS layers:
const createWMS = (id, url, layers, op) => {
  if (typeof op === 'undefined' || op === null) { op = 0.75; }
  return {
    // I am forced to multiple layers according to projection, oterwise openlayers
    // will not manage to show them correctly when maps have different projections
    image: {
      dynamicProjection: createImageWMS(id, url, layers, 'auto', op),
      'EPSG:25832': createImageWMS(id, url, layers, '32', op),
      'EPSG:25833': createImageWMS(id, url, layers, '33', op),
      'EPSG:25835': createImageWMS(id, url, layers, '35', op)
    },
    tile: createTileWMS(id, url, layers, op)
  };
};

// The only problem is that it does not work well for minimaps
const createImageWMS = (id, url, layers, utm, op) => {
  const projection = 'EPSG:258' + utm;
  return new ImageLayer({
    properties: { id, type: 'wms' },
    opacity: op,
    source: new ImageWMS({
      crossOrigin: '',
      url: url,
      params: { LAYERS: layers },
      serverType: 'geoserver',
      projection: (utm === 'auto') ? '' : projection
    })
  });
};

// The only problem is that it is clipped on current projection
const createTileWMS = (id, url, layers, op) => {
  return new TileLayer({
    properties: { id, type: 'wms' },
    opacity: op,
    source: new TileWMS({
      crossOrigin: 'anonymous',
      url: url,
      params: { LAYERS: layers, TILED: true },
      serverType: 'mapserver',
      tileGrid: new TilegridWMTS({
        origin: getTopLeft(mapExtent['33']),
        resolutions: gridResolutions,
        matrixIds: matrixIds['33']
      }),
    })
  });
};

const setLimits = (id, min, max) => {
  const wms = layer[id];
  wms.tile.setMinResolution(min);
  wms.tile.setMaxResolution(max);
  for (const [projection, imageLayer] of Object.entries(wms.image)) {
    imageLayer.setMinResolution(min);
    imageLayer.setMaxResolution(max);
  }
};

const getWMSserver = () =>  {
  const server = window.location.href.split('/')[2];
  let wms = 'wms';
  if (server.indexOf('utv') + server.indexOf('localhost') > -2)
    wms += '04utv.int';
  if (server.indexOf('test') > -1) wms += '04test';
  return 'https://' + wms + '.nibio.no/cgi-bin/';
};
const wmsServer = getWMSserver();

// WMS ar5
layer.ar5 = createWMS(
  'ar5', wmsServer + 'ar5', 'arealtype,AR5grenser', 0.5
);
setLimits('ar5', 0, 18); // to avoid empty responses from wms.nibio.no

// WMS grunnkart skogbonitet
layer.skogbonitet = createWMS(
  'skogbonitet', wmsServer + 'klimagasskalkulator', 'skogbonitet', 0.5
);
setLimits('skogbonitet', 0, 10); // to avoid empty responses from wmsServer

// WMS grunnkart
layer.grunnkart = createWMS(
  'grunnkart', wmsServer + 'klimagasskalkulator',
  // 'arealbruk,treslag,skogbonitet,organisk,bebygd,kraftlinje', 0.5
  'arealbruk,treslag,organisk,bebygd,kraftlinje', 0.5
);
setLimits('grunnkart', 0, 10); // to avoid empty responses from wmsServer

// WFS Tønsberg og Drammen
const featureRequest = (epsg) => new WFS({ version: '1.0.0' }).writeGetFeature({
  srsName: epsg,
  featureNS: 'https://kart.tonsberg.kommune.no/WebInnsyn/api/wfs',
  featurePrefix: 'gmgml',
  featureTypes: ['klimagassnedbygging'],
  filter: and(
    equalTo('co2_uendretarealbruk_kode', 2),
    equalTo('co2_nedbygd_klasse', 3)
  )
});
const tvs = new VectorSource({
  format: new GML2(),
  loader: (extent, resolution, projection, success, failure) => {
    const epsg = projection.getCode();
    const url = 'https://kart.tonsberg.kommune.no/WebInnsyn/api/wfs?' +
      'theme1=Klima&srsname=' + epsg + '&bbox=' + extent.join(',');
    // const xhr = new XMLHttpRequest();
    // // xhr.open('GET', url);
    // xhr.open('POST', url);
    // const onError = () => {
    //   tvs.removeLoadedExtent(extent);
    //   failure();
    // };
    // xhr.onerror = onError;
    // xhr.onload = () => {
    //   if (xhr.status == 200) {
    //     const features = tvs.getFormat().readFeatures(xhr.responseText);
    //     tvs.addFeatures(features);
    //     success(features);
    //   } else onError();
    // };
    // // xhr.send(); // GET
    // xhr.send(new XMLSerializer().serializeToString(featureRequest(epsg)));
    fetch(url, {
      method: 'POST',
      body: new XMLSerializer().serializeToString(featureRequest(epsg)),
    })
    .then(response => response.text())
    .then(gml => tvs.addFeatures(tvs.getFormat().readFeatures(gml)));
  },
  // url: (extent, resolution, projection) =>
  //     'https://kart.tonsberg.kommune.no/WebInnsyn/api/wfs?' +
  //     'theme1=Klima&service=WFS&request=GetFeature&version=1.0.0&' +
  //     'typename=gmgml:klimagassnedbygging&' +
  //     'srsname=' + projection.getCode() + '&bbox=' + extent.join(','),
  //     // '&filter=<?xml version="1.0"?><Filter xmlns="http://www.opengis.net/ogc"><And><PropertyIsEqualTo><PropertyName>co2_uendretarealbruk_kode</PropertyName><Literal>2</Literal></PropertyIsEqualTo><PropertyIsEqualTo><PropertyName>co2_nedbygd_klasse</PropertyName><Literal>3</Literal></PropertyIsEqualTo></And></Filter>',
  //     // '&cql_filter=co2_uendretarealbruk_kode=2 AND co2_nedbygd_klasse=3',
  strategy: bbox
});
layer.tønsberg = new VectorLayer({
  properties: { id: 'tønsberg', type: 'wfs' },
  opacity: 0.5,
  source: tvs,
  style: {
    'stroke-width': 2,
    'stroke-color': 'blue',
    'fill-color': 'rgba(200, 200, 200, 1)',
  },
  visible: false
});
// setLimits('tønsberg', 0, 10); // to avoid huge responses from wfs server

const geonorge = 'https://wms.geonorge.no/skwms1/wms.';
layer.eiendom = createWMS(
  'eiendom', geonorge + 'matrikkel', 'eiendomsgrense,grensepunkt', 1
);
layer.gnr = createWMS('gnr', geonorge + 'matrikkel', 'eiendoms_id', 1);
// layer.histOk = createWMS(
//   'histOk', 'https://kilden.nibio.no/bakgrunn/skwms1/wms.n5raster2',
//   'n5raster_foerstegang', 1
// );
// layer.hoyde = createWMS(
//   'hoyde', geonorge + 'topo4',
//   'N2000Hoydekurver,N1000Hoydekurver,N500Hoydekurver,N250Hoydekurver,N50Hoydekurver,hoydekurver_5m,hoydekurver_1m,hoydetall5linje,hoydetall5punkt', 1
// );
// Kommuneplan
layer.kp = createWMS( // Nåværende kommuneplan
  'kp', geonorge + 'kommuneplaner', 'Arealformal_kp', 0.5
);
setLimits('kp', 0, 40); // to avoid empty responses from wms server
layer.kpf = createWMS( // Forslag til kommuneplan
  'kpf', geonorge + 'kommuneplanforslag', 'Arealformal_kp', 0.5
);
setLimits('kpf', 0, 40); // to avoid empty responses from wms server
layer.kpp = createWMS( // Planområde til kommuneplan
  'kpp', geonorge + 'kommuneplanforslag', 'Planomrade_kp', 0.8
);
// setLimits('kpp', 0, 120); // to avoid empty responses from wms server
// Kommunedelplan
layer.kdp = createWMS( // Nåværende kommunedelplan
  'kdp', geonorge + 'kommuneplaner', 'Arealformal_kdp', 0.5
);
setLimits('kdp', 0, 40); // to avoid empty responses from wms server
layer.kdpf = createWMS( // Forslag til kommunedelplan
  'kdpf', geonorge + 'kommuneplanforslag', 'Arealformal_kdp', 0.5
);
setLimits('kdpf', 0, 40); // to avoid empty responses from wms server
layer.kdpp = createWMS( // Planområde til kommunedelplan
  'kdpp', geonorge + 'kommuneplanforslag', 'Planomrade_kdp', 0.8
);
// setLimits('kdpp', 0, 120); // to avoid empty responses from wms server
// Reguleringsplan
layer.rp = createWMS( // Nåværende reguleringsplan
  'rp', geonorge + 'reguleringsplaner', 'Arealformal_02', 0.5
);
setLimits('rp', 0, 12); // to avoid empty responses from wms server
layer.rpf = createWMS( // Forslag til reguleringsplan
  'rpf', geonorge + 'reguleringsplanforslag', 'Arealformal_vn2', 0.5
);
setLimits('rpf', 0, 12); // to avoid empty responses from wms server
layer.rpp = createWMS( // Planområde til reguleringsplan
  'rpp', geonorge + 'reguleringsplanforslag', 'Planomrade_vn2', 0.8
);
setLimits('rpp', 0, 120); // to avoid empty responses from wms server

// WMS autorisert planområde (needs request_id, linked to plan computation)
layer.authorized = createWMS(
  'authorized', wmsServer + 'klimagasskalkulator', 'autorisert_planomrade', 0.8
);
// setLimits('authorized', 0, 10); // to avoid empty responses from wmsServer

const getTileWMS = (id) => layer[id]?.tile;
export const getWMS = (id) => getTileWMS(id) || layer[id]?.image.dynamicProjection;
export const getWFS = (id) => layer[id];

